import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import { get, map } from 'lodash';
import { RootState } from 'modules';
import { useRecoilValue } from 'recoil';
import scrollIntoView from 'scroll-into-view';
import OrderingButton from 'components/base/OrderingButton';
import SearchInput from 'components/base/SearchInput';
import apiClient from 'libs/api/apiClient';
import { usePagination, useDataApi } from 'libs/hooks';
import { resetState } from 'modules/learning/createQuiz';
import { quizListFilterState } from 'modules/learning/recoilState';
import { TListResponse, TPagination } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { Card, Row, List, Button, Badge, Tag, Typography } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PrimaryColor, device, StyledCheckIcon } from 'GlobalStyles';
import quizListTop from 'assets/images/banners/13.webp';
import ExtendModal from './components/ExtendModal';
import QuizListSearchInput from './components/parts/QuizListSearchInput';
import QuizListFilterModal from './components/QuizListFilterModal';

const FilterTag: any = {
  fit_interview: 'Fit Interview',
  case_interview: 'Case Interview',
  growth_strategy: 'Growth Strategy',
  turnaround_strategy: 'Turnaround Strategy',
  dd_market_sizing: 'DD & Market Sizing',
  operation_optimization: 'Operation Optimization',
  sales_marketing_strategy: 'Sales & Marketing Strategy',
  clarification: 'Clarification',
  structuring: 'Structuring',
  analysis: 'Analysis',
  solution_wrap_up: 'Solution & Wrap up',
  full_drill: '세트문제',
};

type TQuiz = {
  id: number;
  subject: string;
  quiz_types: string;
  is_completed: boolean;
  quiz_number: number;
  title: string;
  hit_count: number;
  is_new: string;
  correct_rate: number;
  is_user_solved: string;
  is_full_drill: boolean;
};

interface ICardBadge {
  quizId: boolean;
}
interface IScreen {
  width: string;
}

const QuizList = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [isFirstRendering, setIsFirstRendering] = useState(true);
  const [quizListModalData, setQuizListModalData]: any = useState({
    visible: false,
  });
  const [filterTagList, setFilterTagList] = useState<Array<string>>([]);
  const [pagination, setPagination] = usePagination(15);
  const [isSearchInput, setIsSearchInput] = useState(false);
  const quizFilters = useRecoilValue(quizListFilterState);
  const { Title, Text } = Typography;
  const dispatch = useDispatch();

  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };

  const getQuizList = ({ params }: any, cancel?: CancelTokenSource) => {
    return apiClient.get<TQuiz>(`/drill/quizs`, {
      params: params,
      cancelToken: cancel?.token,
    });
  };

  const getPromise = getQuizList.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
      ordering: pagination.ordering ? pagination.ordering : '-quiz_number',
      is_full_drill: quizFilters.is_full_drill.join('+'),
      subjects: quizFilters.subject.join('+'),
      quiz_types: quizFilters.quiz_types.join('+'),
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TQuiz>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [quizFilters, pagination]);

  function upToScroll(loading: boolean, isFirstRendering: boolean) {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }

  useEffect(() => {
    upToScroll(loading, isFirstRendering);
  }, [loading]);

  //어드민 문제 생성할 때 문제 번호, 문제 제목 초기화시켜주기 위해 필요
  useEffect(() => {
    dispatch(resetState());
  }, []);

  return (
    <>
      <ExtendModal />
      <StyledContainer>
        <div className='title_wrapper'>
          <Title className='pageTitle'>Career Step 오리지널 케이스</Title>
          <Text className='pageSubtitle'>
            실전과 유사한 케이스 문제를 직접 풀어보세요{<br />}2주마다 새로운 5문제가 업데이트 됩니다.
          </Text>
        </div>
        <StyledQuizListHeader id='card-list-header'>
          <TotalQuiz width='lg'>{`총 ${get(data, 'total')}개 문제`}</TotalQuiz>
          <SearchInputArea width='lg'>
            <SearchInput pagination={pagination} reloadPage={reloadPage} placeHolder='검색어 입력' />
          </SearchInputArea>
          <SearchInputArea width='md'>
            <div
              onClick={() => setIsSearchInput(true)}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <SearchOutlined
                style={{
                  display: isSearchInput ? 'none' : 'block',
                  fontSize: '16px',
                }}
              />
            </div>

            <QuizListSearchInput
              placeHolder='검색어 입력'
              suffix={<CloseOutlined onClick={() => setIsSearchInput(false)} style={{ color: '#003159' }} />}
              pagination={pagination}
              reloadPage={reloadPage}
              style={{
                display: isSearchInput ? 'flex' : 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                minWidth: '100%',
                height: '40px',
                zIndex: 1,
              }}
            />
          </SearchInputArea>
          <StyledFilterBtns>
            <Badge
              dot={
                quizFilters.subject.length > 0 ||
                quizFilters.quiz_types.length > 0 ||
                quizFilters.is_full_drill.length > 0
              }
            >
              <Button
                size='small'
                onClick={() => {
                  setQuizListModalData({
                    visible: true,
                    onFinish: (data: any) => {
                      reloadPage({
                        current: 1,
                        pageSize: pagination.pageSize,
                        is_full_drill: get(data, 'is_full_drill'),
                        subject: get(data, 'subject'),
                        quiz_types: get(data, 'quiz_types'),
                      });
                      setFilterTagList(get(data, 'tagNames', []));
                    },
                  });
                }}
              >
                필터
              </Button>
            </Badge>
            <OrderingButton
              pagination={pagination}
              reloadPage={reloadPage}
              defaultKey='-quiz_number'
              orderingList={{
                '-quiz_number': '최신순',
                quiz_number: '오래된순',
                '-hit_count_generic__hits': '조회수 높은 순',
                hit_count_generic__hits: '조회수 낮은 순',
                '-correct_rate': '정답률 높은 순',
                correct_rate: '정답률 낮은 순',
              }}
            />

            <QuizListFilterModal modalState={quizListModalData} setModalState={setQuizListModalData} />
          </StyledFilterBtns>
        </StyledQuizListHeader>
        <TotalQuiz width='md'>{`총 ${get(data, 'total')}개 문제`}</TotalQuiz>
        <StyledTagBox>
          {quizFilters.tagNames.length > 0
            ? map(quizFilters.tagNames, (v) => (
                <Tag className='filter-tag' visible={true}>
                  {FilterTag[v]}
                </Tag>
              ))
            : map(filterTagList, (v) => (
                <Tag className='filter-tag' visible={true}>
                  {FilterTag[v]}
                </Tag>
              ))}
        </StyledTagBox>
        {(currentUser?.userid === 'admin@admin.com' ||
          currentUser?.userid === 'han.jongho@bigstepedu.com' ||
          currentUser?.userid === 'oh.jongkwan@bigstepedu.com' ||
          currentUser?.userid === 'kwon.dogeun@bigstepedu.com' ||
          currentUser?.userid === 'dogeun.kwon@gmail.com') && (
          <Link to={`/case/quiz/create`}>
            <Button className='outline' style={{ marginRight: '5px' }}>
              문제 생성
            </Button>
          </Link>
        )}
        <StyledQuizBox>
          <List
            pagination={{
              ...pagination,
              total: get(data, 'total', 0),
              position: 'bottom',
              showSizeChanger: false,
              onChange: (page) => {
                reloadPage({
                  current: page,
                  pageSize: pagination.pageSize,
                });
              },
            }}
            className='card'
            loading={loading}
            style={{ paddingBottom: '20px', marginTop: '30px' }}
            grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }}
            dataSource={get(data, 'contents')}
            renderItem={(item) => (
              <List.Item>
                <Link to={`/case/quiz/view/${get(item, 'id')}`}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: '10px',
                      backgroundColor:
                        get(item, 'is_user_solved') === 'true' ? 'rgba(174, 174, 174, 0.2)' : '#ffffff',
                    }}
                  >
                    <Row justify='space-between'>
                      <Row>
                        <StyledQuizTopBadge quizId={true}>{get(item, 'quiz_number')}</StyledQuizTopBadge>
                        {get(item, 'is_new') === 'true' && (
                          <StyledQuizTopBadge quizId={false}>New</StyledQuizTopBadge>
                        )}
                        {get(item, 'is_full_drill') && (
                          <StyledQuizTopBadge quizId={false} style={{ backgroundColor: '#3CAC40' }}>
                            세트문제
                          </StyledQuizTopBadge>
                        )}

                        {(currentUser?.userid === 'admin@admin.com' ||
                          currentUser?.userid === 'han.jongho@bigstepedu.com' ||
                          currentUser?.userid === 'oh.jongkwan@bigstepedu.com' ||
                          currentUser?.userid === 'kwon.dogeun@bigstepedu.com' ||
                          currentUser?.userid === 'dogeun.kwon@gmail.com') &&
                          !get(item, 'is_completed') && (
                            <StyledQuizTopBadge quizId={false}>임시 저장</StyledQuizTopBadge>
                          )}
                      </Row>
                      <Row>
                        {get(item, 'is_user_solved') === 'true' && (
                          <>
                            <StyledQuizComplete>풀이완료</StyledQuizComplete>
                            <StyledCheckIcon
                              style={{
                                position: 'static',
                                width: '20px',
                                height: '20px',
                                marginLeft: '2px',
                                borderRadius: '50%',
                                backgroundColor: '#3CAC40',
                              }}
                            />
                          </>
                        )}
                      </Row>
                    </Row>
                    <StyledQuizTitle>{get(item, 'title')}</StyledQuizTitle>
                    <Row justify='space-between' align='middle' style={{ marginTop: '20px' }}>
                      <div>{`조회 수 ${get(item, 'hit_count')}`}</div>
                      {get(item, 'is_user_solved') === 'true' ? (
                        <Row align='middle'>
                          {(currentUser?.userid === 'admin@admin.com' ||
                            currentUser?.userid === 'han.jongho@bigstepedu.com' ||
                            currentUser?.userid === 'oh.jongkwan@bigstepedu.com' ||
                            currentUser?.userid === 'kwon.dogeun@bigstepedu.com' ||
                            currentUser?.userid === 'dogeun.kwon@gmail.com') && (
                            <Link to={`/case/quiz/create/${get(item, 'id')}`}>
                              <Button style={{ marginRight: '5px' }}>수정</Button>
                            </Link>
                          )}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '96px',
                              height: '29px',
                              borderRadius: '8px',
                              backgroundColor: '#747474',
                              color: '#ffffff',
                            }}
                          >
                            다시풀기
                          </div>
                        </Row>
                      ) : (
                        <Row align='middle'>
                          {(currentUser?.userid === 'admin@admin.com' ||
                            currentUser?.userid === 'han.jongho@bigstepedu.com' ||
                            currentUser?.userid === 'oh.jongkwan@bigstepedu.com' ||
                            currentUser?.userid === 'kwon.dogeun@bigstepedu.com' ||
                            currentUser?.userid === 'dogeun.kwon@gmail.com') && (
                            <Link to={`/case/quiz/create/${get(item, 'id')}`}>
                              <Button style={{ marginRight: '5px' }}>수정</Button>
                            </Link>
                          )}
                          <div>{`정답률 ${get(item, 'correct_rate')}%`}</div>
                        </Row>
                      )}
                    </Row>
                  </Card>
                </Link>
              </List.Item>
            )}
          />
        </StyledQuizBox>
      </StyledContainer>
    </>
  );
};

export default QuizList;

const maxWidth = CWindowWidth - 300;
const StyledContainer = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .title_wrapper {
    width: 100%;
    height: 400px;
    padding: 0 10px;
    background-image: url(${quizListTop});
    background-size: cover;

    @media screen and ${device.md} {
      height: 205px;
    }

    .pageTitle {
      margin: 150px auto 0 auto;
      font-size: 56px;
      font-weight: 700;

      @media screen and ${device.md} {
        margin-top: 60px;
        font-size: 30px;
      }
    }

    .pageSubtitle {
      margin: 16px auto 0 auto;
      font-size: 14px;

      @media screen and ${device.md} {
        font-size: 10px;
      }
    }

    .ant-typography {
      display: block;
      max-width: ${maxWidth}px;
      color: #ffffff;
    }
  }
`;

const StyledQuizBox = styled.div`
  max-width: ${maxWidth}px;
  width: 97%;

  .card .ant-avatar {
    object-fit: contain;
    height: 200px;
  }

  .card .ant-card-body {
    min-height: 120px;
    padding: 16px 20px;
  }
`;

const StyledQuizTopBadge = styled.div<ICardBadge>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  margin-bottom: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  background-color: ${({ quizId }) => (quizId ? '#fbbc05' : '#de4949')};
`;

const StyledQuizComplete = styled.div`
  padding: 2px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #3cac40;
`;

const StyledQuizTitle = styled.div`
  display: -webkit-box;
  font-size: 18px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const StyledTagBox = styled.div`
  max-width: ${maxWidth}px;
  width: 97%;
  height: 40px;
  margin-top: 10px;

  .filter-tag {
    background: transparent;
    border-radius: 8px;
    border: 1px solid ${PrimaryColor};
    padding: 3px 10px;
    color: ${PrimaryColor};
    font-size: 13px;

    .anticon-close {
      color: ${PrimaryColor};
    }
  }

  @media screen and ${device.md} {
    display: none;
  }
`;

const ListWrapper = styled.div`
  & .ant-btn {
    display: block;
    margin-bottom: 20px;
  }
`;

const StyledQuizListHeader = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  max-width: ${maxWidth}px;
  width: 97%;
  margin-top: 36px;
  & .ant-input-affix-wrapper {
    transform: translateX(-25%);
    @media screen and ${device.md} {
      transform: unset;
    }
  }
`;

const TotalQuiz = styled.div<IScreen>`
  display: ${({ width }) => width === 'md' && 'none'};
  width: 33%;

  @media screen and ${device.md} {
    display: ${({ width }) => (width === 'md' ? 'block' : 'none')};
    width: 97%;
    margin-top: 20px;
  }
`;

const StyledFilterBtns = styled.div`
  display: flex;
  justify-content: end;

  /* width: 33%; */
`;

const SearchInputArea = styled.div<IScreen>`
  display: flex;
  display: ${({ width }) => width === 'md' && 'none'};
  justify-content: center;
  align-items: center;
  width: 33%;
  width: ${({ width }) => width === 'md' && '40px'};
  height: ${({ width }) => width === 'md' && '40px'};
  border-radius: ${({ width }) => width === 'md' && '7px'};
  background-color: ${({ width }) => width === 'md' && '#f9f9f9'};

  @media screen and ${device.md} {
    display: ${({ width }) => (width === 'md' ? 'flex' : 'none')};
    cursor: pointer;
  }
`;
